// PreviewDoSite.tsx
//
// O preview da landing. Ele NÃO desenha nada por conta própria: materializa a
// mesma lista de blocos que o alfredo-pedidos renderiza para o cliente final,
// com o mesmo tema. É o mesmo contrato do PreviewComanda, que materializa as
// linhas do renderizador compartilhado em vez de redesenhar a bobina.
//
// A garantia de que os dois não divergem é a trava de hash do `src/comum/site`
// (ferramentas/verificarSite.js, no prebuild): se o catálogo mudar aqui e não
// lá, o build quebra.
//
// Isto é um ESBOÇO fiel, não um espelho pixel a pixel — o site real é
// server-side, com fonte e escala próprias. O que ele precisa acertar é ORDEM,
// CONTEÚDO e COR, que é onde o dono comete erro.

import React from "react";
import {
  BlocoDoSite,
  MarcaDaLanding,
  TemaDoSite,
  UnidadeDaLanding,
  sanitizarHtml,
} from "../../../../comum/site";

interface Props {
  tema: TemaDoSite;
  marca: MarcaDaLanding;
  unidades: UnidadeDaLanding[];
  blocos: BlocoDoSite[];
}

const RAIO: Record<TemaDoSite["cantos"], number> = {
  reto: 0,
  suave: 12,
  redondo: 24,
};

const FONTE: Record<TemaDoSite["tipografia"], string> = {
  moderna: '"Space Grotesk", system-ui, sans-serif',
  classica: 'Georgia, "Times New Roman", serif',
  manuscrita: '"Trebuchet MS", "Segoe UI", sans-serif',
};

function texto(config: Record<string, unknown>, chave: string): string {
  const valor = config[chave];
  return typeof valor === "string" ? valor : "";
}

function lista<T>(config: Record<string, unknown>, chave: string): T[] {
  const valor = config[chave];
  return Array.isArray(valor) ? (valor as T[]) : [];
}

const PreviewDoSite: React.FC<Props> = ({ tema, marca, unidades, blocos }) => {
  const cor = tema.marca;
  const raio = RAIO[tema.cantos];
  const fonte = FONTE[tema.tipografia];

  const titulo = (conteudo: string, centro = false) => (
    <div style={{ textAlign: centro ? "center" : "left" }}>
      <h2
        style={{
          margin: 0,
          fontFamily: fonte,
          fontSize: "var(--fs-xl)",
          color: cor,
        }}
      >
        {conteudo}
      </h2>
      <div
        style={{
          height: 3,
          width: 40,
          marginTop: 8,
          marginLeft: centro ? "auto" : 0,
          marginRight: centro ? "auto" : 0,
          borderRadius: 999,
          backgroundColor: cor,
        }}
      />
    </div>
  );

  const secao = (conteudo: React.ReactNode, faixa = false, chave?: string) => (
    <div
      key={chave}
      style={{
        padding: "var(--sp-20) var(--sp-16)",
        backgroundColor: faixa ? "#f7f8f7" : "#ffffff",
      }}
    >
      {conteudo}
    </div>
  );

  return (
    <div style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>
      <div style={{ height: 4, backgroundColor: cor }} />

      {blocos.map((bloco) => {
        const c = bloco.config ?? {};

        switch (bloco.tipo) {
          case "capa": {
            /*
             * A CAPA MOSTRA O LOGO E A FOTO DE FUNDO.
             *
             * Ela ignorava os dois. O descritor do bloco declara `logoUrl` e
             * `imagemFundo` desde sempre, e o editor agora deixa ENVIAR a foto
             * — mas a prévia continuava desenhando só título e frase sobre a cor
             * da marca. Na prática: a pessoa subia a foto da fachada, olhava ao
             * lado e nada mudava. Dar o envio sem mostrar o resultado é meia
             * funcionalidade.
             *
             * O `altura` também é obedecido aqui (o descritor oferece "Média" e
             * "Tela cheia"). Como isto é uma PRÉVIA dentro de uma moldura de
             * celular, "tela cheia" vira uma proporção alta e não 100dvh — a
             * moldura tem a altura que tem.
             */
            const fundo = texto(c, "imagemFundo");
            const logo = texto(c, "logoUrl");
            const alta = texto(c, "altura") === "alta";
            /* Sobre foto, o texto precisa de um véu escuro para se ler: uma
               frase clara sobre um salão claro desaparece. Sem foto, fica a cor
               da marca e o véu não existe. */
            const sobreFoto = Boolean(fundo);
            return (
              <div
                key={bloco.blocoUuid}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  minHeight: alta ? 260 : 160,
                  padding: "var(--sp-32) var(--sp-16)",
                  backgroundColor: tema.marcaFundo,
                  backgroundImage: fundo ? `url(${fundo})` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  textAlign: "center",
                }}
              >
                {sobreFoto && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0, 0, 0, 0.42)",
                    }}
                  />
                )}
                {logo && (
                  <img
                    src={logo}
                    alt=""
                    style={{
                      position: "relative",
                      maxWidth: "45%",
                      maxHeight: 64,
                      objectFit: "contain",
                    }}
                  />
                )}
                <div
                  style={{
                    position: "relative",
                    fontFamily: fonte,
                    fontSize: "var(--fs-2xl)",
                    fontWeight: 600,
                    color: sobreFoto ? "#ffffff" : cor,
                  }}
                >
                  {texto(c, "titulo") || marca.nome}
                </div>
                {texto(c, "frase") && (
                  <div
                    style={{
                      position: "relative",
                      color: sobreFoto ? "rgba(255, 255, 255, 0.92)" : "#5a6560",
                    }}
                  >
                    {texto(c, "frase")}
                  </div>
                )}
              </div>
            );
          }

          case "pedir":
            return (
              <div
                key={bloco.blocoUuid}
                style={{
                  padding: "var(--sp-20) var(--sp-16)",
                  backgroundColor: tema.marcaFundo,
                  textAlign: "center",
                }}
              >
                {texto(c, "chamada") && (
                  <div style={{ marginBottom: 10, color: "#5a6560" }}>
                    {texto(c, "chamada")}
                  </div>
                )}
                {/* Verde de propósito: a cor de AÇÃO é a do sistema, não a do
                    dono — o contraste dela é calibrado para AA. */}
                <span
                  style={{
                    display: "inline-block",
                    padding: "10px 24px",
                    borderRadius: raio || 6,
                    backgroundColor: "#26792f",
                    color: "#ffffff",
                    fontWeight: 600,
                  }}
                >
                  {texto(c, "rotulo") || "Peça agora"}
                </span>
                {unidades.length > 1 && (
                  <div style={{ marginTop: 8, color: "#656e68" }}>
                    {unidades.length} unidades — escolha em qual você quer pedir.
                  </div>
                )}
              </div>
            );

          case "cardapio":
            return secao(
              <div style={{ textAlign: "center" }}>
                {titulo(texto(c, "titulo") || "Cardápio", true)}
                <span
                  style={{
                    display: "inline-block",
                    marginTop: 16,
                    padding: "10px 24px",
                    borderRadius: raio || 6,
                    backgroundColor: cor,
                    color: tema.marcaTexto === "claro" ? "#ffffff" : "#16211c",
                    fontWeight: 600,
                  }}
                >
                  Ver o cardápio
                </span>
              </div>,
              false,
              bloco.blocoUuid
            );

          case "unidades":
            return secao(
              <>
                {titulo(texto(c, "titulo") || "Onde estamos")}
                <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                  {unidades.map((unidade) => (
                    <div
                      key={unidade.subsidiaryUuid}
                      style={{
                        padding: "var(--sp-12)",
                        borderRadius: raio || 8,
                        border: "1px solid #e2e6e3",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <div style={{ fontFamily: fonte, fontWeight: 600 }}>{unidade.nome}</div>
                      {unidade.endereco && (
                        <div style={{ color: "#5a6560" }}>{unidade.endereco}</div>
                      )}
                      {unidade.telefone && (
                        <div style={{ marginTop: 6, color: cor, fontWeight: 600 }}>
                          {unidade.telefone}
                        </div>
                      )}
                    </div>
                  ))}
                  {unidades.length === 0 && (
                    <div style={{ color: "#656e68" }}>Nenhuma unidade cadastrada.</div>
                  )}
                </div>
              </>,
              true,
              bloco.blocoUuid
            );

          case "horarios":
            return secao(
              <>
                {titulo(texto(c, "titulo") || "Horários")}
                <div style={{ marginTop: 12, color: "#5a6560" }}>
                  {unidades.filter((u) => u.horarios.length > 0).length > 0
                    ? unidades
                        .filter((u) => u.horarios.length > 0)
                        .map((u) => u.nome)
                        .join(" · ")
                    : "Nenhuma unidade tem horário cadastrado ainda — a seção não aparece no site."}
                </div>
              </>,
              false,
              bloco.blocoUuid
            );

          case "links":
            return secao(
              <>
                {texto(c, "titulo") && titulo(texto(c, "titulo"), true)}
                <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                  {lista<{ rotulo: string }>(c, "itens").map((item, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "var(--sp-10)",
                        borderRadius: raio || 8,
                        border: "1px solid #e2e6e3",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      {item.rotulo}
                    </div>
                  ))}
                </div>
              </>,
              false,
              bloco.blocoUuid
            );

          case "redes":
            return secao(
              <div style={{ textAlign: "center", color: "#5a6560" }}>
                {lista<{ rede: string }>(c, "itens")
                  .map((item) => item.rede)
                  .join(" · ") || "Nenhuma rede social cadastrada."}
              </div>,
              false,
              bloco.blocoUuid
            );

          case "avaliacoes":
            return secao(
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: fonte, fontSize: "var(--fs-2xl)", color: cor }}>
                  {typeof c.nota === "number" && c.nota > 0
                    ? String(c.nota).replace(".", ",")
                    : "—"}
                </div>
                <div style={{ color: "#5a6560" }}>
                  {typeof c.total === "number" && c.total > 0
                    ? `${c.total} avaliações no Google`
                    : "Avaliações no Google"}
                </div>
              </div>,
              true,
              bloco.blocoUuid
            );

          case "texto":
            return secao(
              <>
                {texto(c, "titulo") && titulo(texto(c, "titulo"))}
                <div
                  style={{ marginTop: 12, color: "#5a6560" }}
                  // O mesmo sanitizador do servidor, do módulo compartilhado.
                  dangerouslySetInnerHTML={{ __html: sanitizarHtml(texto(c, "html")) }}
                />
              </>,
              false,
              bloco.blocoUuid
            );

          case "galeria":
            return secao(
              <>
                {texto(c, "titulo") && titulo(texto(c, "titulo"))}
                <div
                  style={{
                    marginTop: 12,
                    display: "grid",
                    gap: 6,
                    gridTemplateColumns: `repeat(${
                      typeof c.colunas === "number" ? c.colunas : 3
                    }, 1fr)`,
                  }}
                >
                  {lista<{ url: string; alt: string }>(c, "imagens").map((img, i) => (
                    <img
                      key={i}
                      src={img.url}
                      alt={img.alt}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        borderRadius: raio || 8,
                      }}
                    />
                  ))}
                </div>
              </>,
              false,
              bloco.blocoUuid
            );

          case "marcas":
            return secao(
              <>
                {titulo(texto(c, "titulo") || "Nossos restaurantes")}
                <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                  {marca.marcas.map((item) => (
                    <div
                      key={item.companyUuid}
                      style={{
                        padding: "var(--sp-12)",
                        borderRadius: raio || 8,
                        border: "1px solid #e2e6e3",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontFamily: fonte, fontWeight: 600, color: cor }}>
                        {item.nome}
                      </div>
                      <div style={{ color: "#5a6560" }}>
                        {item.unidades} {item.unidades === 1 ? "unidade" : "unidades"}
                        {!item.handle && " · site em preparação"}
                      </div>
                    </div>
                  ))}
                </div>
              </>,
              false,
              bloco.blocoUuid
            );

          case "rodape":
            return (
              <div
                key={bloco.blocoUuid}
                style={{
                  padding: "var(--sp-20) var(--sp-16)",
                  backgroundColor: "#f7f8f7",
                  borderTop: "1px solid #e2e6e3",
                  color: "#5a6560",
                }}
              >
                <div style={{ fontFamily: fonte, fontWeight: 600, color: "#16211c" }}>
                  {marca.nome}
                </div>
                {texto(c, "copyright") && <div>{texto(c, "copyright")}</div>}
                <div style={{ marginTop: 12, fontSize: "var(--fs-xs)", color: "#656e68" }}>
                  Site e pedidos pelo Alfredo — 100% da venda fica com o restaurante.
                </div>
              </div>
            );

          default:
            // Tipo que este preview não conhece some, igual ao site público.
            return null;
        }
      })}
    </div>
  );
};

export default PreviewDoSite;
